import { useState, useEffect, useCallback } from 'react';

export const useSingleEdit = (
  text: string,
  controlledEditing?: boolean,
  onEditStart?: () => void,
  onEditComplete?: (text: string, wasChanged: boolean) => void,
  onEditCancel?: () => void
) => {
  const [internalEditing, setInternalEditing] = useState(false);
  const [editText, setEditText] = useState(text);

  const isEditingActive =
    controlledEditing !== undefined ? controlledEditing : internalEditing;
  const isControlled = controlledEditing !== undefined;

  useEffect(() => {
    if (!isEditingActive) {
      setEditText(text);
    }
  }, [text, isEditingActive]);

  const startEdit = useCallback(() => {
    if (onEditStart) {
      onEditStart();
    } else {
      setInternalEditing(true);
    }
  }, [onEditStart]);

  const finishEdit = useCallback(
    (finalText: string) => {
      const wasChanged = finalText.trim() !== text.trim();
      if (onEditComplete) {
        onEditComplete(finalText, wasChanged);
      }
      if (!isControlled) {
        setInternalEditing(false);
      }
    },
    [text, onEditComplete, isControlled]
  );

  const cancelEdit = useCallback(() => {
    setEditText(text);
    if (onEditCancel) {
      onEditCancel();
    }
    if (!isControlled) {
      setInternalEditing(false);
    }
  }, [text, onEditCancel, isControlled]);

  return {
    isEditingActive,
    editText,
    setEditText,
    startEdit,
    finishEdit,
    cancelEdit
  } as const;
};
